import { useEffect, useRef, useState } from "react";
import { useSessionContext } from "../context/SessionContext";

const getUrlParams = () => {
  const url = new URL(window.location.href);
  if (url.searchParams && (url.searchParams.has("code") || url.searchParams.has("error"))) {
    return url.searchParams;
  }

  const hash = window.location.hash || "";
  const qIdx = hash.indexOf("?");
  if (qIdx >= 0) return new URLSearchParams(hash.slice(qIdx + 1));

  return new URLSearchParams();
};

const parseJwtPayload = (jwt) => {
  const payload = jwt?.split?.(".")?.[1];
  if (!payload) return null;
  const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
  const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
  try {
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

const tokenNotExpired = (token) => !!(token?.valid_until && token.valid_until > Date.now());

const base64url = (arrayBuffer) => {
  const bytes = new Uint8Array(arrayBuffer);
  let str = "";
  for (const b of bytes) str += String.fromCharCode(b);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
};

const randomString = (len = 64) => {
  const charset = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~";
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => charset[b % charset.length]).join("");
};

const sha256 = async (text) => {
  const data = new TextEncoder().encode(text);
  return await crypto.subtle.digest("SHA-256", data);
};

const buildLoginUrl = async (oauth) => {
  const authorizeUrl = new URL(`${oauth.oauth_server}/protocol/openid-connect/auth`);

  const state = randomString(32);
  const codeVerifier = randomString(64);
  const codeChallenge = base64url(await sha256(codeVerifier));

  sessionStorage.setItem(`pkce_${state}`, codeVerifier);

  authorizeUrl.searchParams.set("response_type", oauth.response_type || "code");
  authorizeUrl.searchParams.set("client_id", oauth.client_id);
  authorizeUrl.searchParams.set("redirect_uri", oauth.callback_url);
  authorizeUrl.searchParams.set("scope", oauth.scope);
  authorizeUrl.searchParams.set("state", state);
  authorizeUrl.searchParams.set("code_challenge", codeChallenge);
  authorizeUrl.searchParams.set("code_challenge_method", "S256");

  return authorizeUrl.toString();
};

const exchangeToken = async (oauth, urlParams) => {
  const code = urlParams.get("code");
  const state = urlParams.get("state");

  const codeVerifier = sessionStorage.getItem(`pkce_${state}`);
  if (!codeVerifier) throw new Error("Missing PKCE code_verifier for state");

  const tokenUrl = `${oauth.oauth_server}/protocol/openid-connect/token`;

  const body = new URLSearchParams({
    grant_type: "authorization_code",
    client_id: oauth.client_id,
    code,
    redirect_uri: oauth.callback_url,
    code_verifier: codeVerifier,
  });

  const res = await fetch(tokenUrl, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });

  if (!res.ok) {
    const txt = await res.text().catch(() => "");
    throw new Error(`Token exchange failed (${res.status}): ${txt}`);
  }

  const data = await res.json();

  const now = Date.now();
  const expiresIn = Number(data.expires_in || 0) * 1000;
  data.valid_until = now + expiresIn - 30_000;

  localStorage.setItem("token", JSON.stringify(data));
  sessionStorage.removeItem(`pkce_${state}`);

  return data;
};

const isAuthorized = (jwt, oauth) => {
  const required = oauth?.entitlement_role;
  if (!required) return true; 

  const ent = jwt?.eduPersonEntitlement;
  if (Array.isArray(ent) && ent.some((x) => String(x).includes(required))) return true;

  const realmRoles = jwt?.realm_access?.roles;
  if (Array.isArray(realmRoles) && realmRoles.includes(required)) return true;

  const clientRoles = jwt?.resource_access?.[oauth.client_id]?.roles;
  if (Array.isArray(clientRoles) && clientRoles.includes(required)) return true;

  return false;
};

const Authentication = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(null);
  const redirectingRef = useRef(false);
  const { env } = useSessionContext();

  useEffect(() => {
    const oauth = env?.oauth;
    if (!oauth?.oauth_server || !oauth?.client_id || !oauth?.callback_url || !oauth?.scope) {
      console.error("[AUTH] Missing oauth configuration", oauth);
      setAuthenticated(false);
      return;
    }

    const params = getUrlParams();

    if (params.has("error")) {
      console.error("[AUTH] OAuth error", {
        error: params.get("error"),
        desc: params.get("error_description"),
      });
      localStorage.removeItem("token");
      setAuthenticated(false);
      return;
    }

    if (params.has("code")) {
      exchangeToken(oauth, params)
        .then(() => {
          window.location.href = oauth.redirect_url;
        })
        .catch((e) => {
          console.error("[AUTH] Error exchanging token", e);
          localStorage.removeItem("token");
          setAuthenticated(false);
        });
      return;
    }

    const stored = localStorage.getItem("token");
    const token = stored ? JSON.parse(stored) : null;

    if (tokenNotExpired(token)) {
      const jwt = parseJwtPayload(token.id_token);
      setAuthenticated(isAuthorized(jwt, oauth));
      return;
    }

    if (!redirectingRef.current) {
      redirectingRef.current = true;
      buildLoginUrl(oauth).then((url) => (window.location.href = url));
    }
  }, [env]);

  if (authenticated === null) return null;
  return <>{authenticated ? children : "Not authenticated"}</>;
};

export default Authentication;
